import { newVector, TypographySettings } from '@lessonup/pins-shared';
import { rem, styled } from '@lessonup/ui-components';
import React, { CSSProperties } from 'react';
import { PieSegment } from './PieSegment';

export interface SpinnerDiscProps {
  size: number;

  /** The titles to display on the segments. */
  segmentTitles: string[];

  /** Rotate the spinner such that the selected index is positioned at the top. */
  selectedIndex?: number;

  /**
   * The number of times this spinner has been spun.
   *
   * This ensures the spinning animation is shown even if a re-spin resulted in
   * the same index.
   */
  spinCount?: number;

  /** Signals when the spinning animation has started or stopped. */
  onAnimationEnd?: (isSpinning: boolean) => void;
  typography: TypographySettings;
  backgroundColors: string[];
  textColors: string[];
}

export function SpinnerDisc(props: SpinnerDiscProps) {
  const titles = props.segmentTitles.length ? props.segmentTitles : [''];
  const count = titles.length;
  const { selectedIndex = 0, spinCount = 0, onAnimationEnd } = props;

  const spinIndex = spinCount * count * 3 + selectedIndex;

  const halfSize = props.size / 2;
  const strokeWidth = 2;

  const spinnerStyle = {
    transform: `rotate(${(-0.5 - spinIndex) / count}turn)`,
    transition: 'transform 2.5s',
  };

  const sweepTurn = 1 / titles.length;

  return (
    <StyledSpinner style={spinnerStyle} onTransitionEnd={() => onAnimationEnd && onAnimationEnd(false)}>
      <SVGWrapper width={props.size} height={props.size} viewBox={`0 0 ${props.size} ${props.size}`}>
        {titles.map((_, index) => {
          return (
            <PieSegment
              key={`segment_${index}`}
              center={newVector(halfSize, halfSize)}
              radius={halfSize - strokeWidth / 2}
              startTurn={index * sweepTurn}
              sweepTurn={sweepTurn}
              fillColor={SegmentHelper.getColor(props.backgroundColors, count, index)}
            />
          );
        })}
      </SVGWrapper>
      {titles.map((title, index) => (
        <SegmentTitle
          key={index}
          style={{
            ...SegmentHelper.getTitleStyle(props.textColors, count, index),
            fontFamily: props.typography.fontFamily,
          }}
        >
          {title}
        </SegmentTitle>
      ))}
    </StyledSpinner>
  );
}

export const SegmentHelper = {
  /** How wide the text title should be, as a fraction of the radius. */
  titleWidthInRadii: 0.75,

  getSliceIndex(slicesLength: number, count: number, index: number): number {
    if (index === count - 1 && index % slicesLength === 0) {
      // Because the first and last segments are adjacent, we want to avoid
      // them having the same colour.
      // If the last segment would have colour index 0, use the next colour (1)
      // instead.
      return 1;
    }

    return index % slicesLength;
  },
  /** Indexes Segment.colors, wrapping around for higher indices. */
  getColor(colors: string[], count: number, index: number): string {
    return colors[SegmentHelper.getSliceIndex(colors.length, count, index)];
  },

  getTitleStyle(colors: string[], count: number, index: number): CSSProperties {
    // Compute how much to translate the element to the right to push it against
    // the edge of the disc. Expressed as a percentage of the title width
    // itself.
    const pushToEdge = `translate(${Math.round(
      ((1 - SegmentHelper.titleWidthInRadii) / SegmentHelper.titleWidthInRadii) * 100
    )}%, 0)`;

    return {
      color: SegmentHelper.getColor(colors, count, index),
      transform: [
        `rotate(${(index + 0.5) / count}turn)`, // account for the index
        `rotate(-0.25turn)`, // start at the top instead of the right
        pushToEdge,
        // rotate around title center:
        'translate(50%, 0)',
        'rotate(0.5turn)',
        'translate(-50%, -50%)',
      ].join(' '),
    };
  },
};

const SVGWrapper = styled.svg`
  width: 100%;
  height: 100%;
`;

const StyledSpinner = styled.div`
  position: relative;
  border-radius: 50%;
  width: 100%;
  height: 100%;
`;

const SegmentTitle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${Math.round((SegmentHelper.titleWidthInRadii / 2) * 100)}%;
  padding-left: 24px;
  white-space: nowrap;
  overflow: hidden;
  transform-origin: top left;
  mask-image: linear-gradient(to right, rgba(0, 0, 0, 1) 75%, rgba(0, 0, 0, 0) 100%);
  font-size: ${rem('24px')};
`;
